import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import BrandIcon from '../assets/DWA_WolfHead_Favicon_Web.png';
import { useAuth } from '../contexts/AuthContext';
import { callMcpTool } from '../services/mcpApi';
import type { ClientAccessEntry } from '../types';
import {
  fetchOverview,
  fetchMoveIns,
  fetchLeads,
  fetchPortfolios,
  mapBackendClientId,
  type DashboardOverview,
  type DashboardMoveIn,
  type DashboardLead,
  type PortfolioDef,
} from '../services/dashboardApi';
import { WeeklyReportView, type WeeklyReportTheme } from '../components/WeeklyReportView';
import { MonthlyReportView, type MonthlyReportTheme } from '../components/MonthlyReportView';
import { CustomReportView, type CustomReportTheme } from '../components/CustomReportView';

type ReportView = 'weekly' | 'monthly' | 'yoy' | 'custom';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OccupancyData {
  _mock?: boolean;
  community?: string;
  totalUnits?: number;
  occupiedUnits?: number;
  occupancyRate?: number;
  byType?: {
    independentLiving?: { total: number; occupied: number };
    assistedLiving?: { total: number; occupied: number };
    memoryCare?: { total: number; occupied: number };
  };
  asOf?: string;
}

interface Lead {
  id: string;
  prospectName: string;
  inquiryDate: string;
  status: string;
  careType: string;
  source?: string;
}

interface LeadsData {
  _mock?: boolean;
  leads?: Lead[];
  total?: number;
}

interface MoveIn {
  id: string;
  residentName: string;
  moveInDate: string;
  careType: string;
}

interface MoveInsData {
  _mock?: boolean;
  moveIns?: MoveIn[];
  total?: number;
}

interface TalkFurtherData {
  _mock?: boolean;
  summary?: {
    totalLeads: number;
    totalTours: number;
    totalConversions: number;
    conversionRate: number;
  };
  leads?: Lead[];
  tours?: Array<{ id: string; prospectName: string; scheduledDate: string; status: string; careType: string; tourType: string }>;
  conversions?: Array<{ id: string; residentName: string; moveInDate: string; careType: string; daysToConvert: number }>;
}

interface YardiFinancial {
  _mock?: boolean;
  property?: string;
  period?: string;
  revenue?: { residential: number; ancillary: number; total: number };
  expenses?: { total: number };
  netOperatingIncome?: number;
  noi_margin?: number;
}

interface DashboardData {
  occupancy: OccupancyData | null;
  leads: LeadsData | null;
  moveIns: MoveInsData | null;
  talkFurther: TalkFurtherData | null;
  financial: YardiFinancial | null;
  isMock: boolean;
  lastUpdated: Date;
}

// ─── Theme ────────────────────────────────────────────────────────────────────

interface ClientTheme {
  label: string;
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  /** Primary accent: metric highlights, refresh button, financial emphasis */
  accent: string;
  /** Section header text */
  sectionTitle: string;
  /** Section header underline */
  sectionDivider: string;
  /** Occupancy bar — healthy rate */
  barHigh: string;
  /** Occupancy bar — mid rate */
  barMid: string;
  /** Secondary highlight: conversions, move-in dates, etc. */
  highlight: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  rowDivider: string;
  barTrack: string;
  financialRowBg: string;
}

import type { DashboardClientId } from './dashboardRouting';

const CLIENT_THEME: Record<DashboardClientId, ClientTheme> = {
  'morningstar-senior-living': {
    label: 'MorningStar Senior Living',
    pageBg: '#f7f6f2',
    cardBg: '#ffffff',
    cardBorder: 'rgba(0,0,0,0.08)',
    cardShadow: '0 1px 4px rgba(0,0,0,0.06)',
    accent: '#A06820',          // Darkened bronze — readable on white
    sectionTitle: '#5C6B44',    // Dark sage green
    sectionDivider: 'rgba(92,107,68,0.2)',
    barHigh: '#5C6B44',         // Dark sage for healthy occupancy
    barMid: '#A06820',          // Dark bronze for mid occupancy
    highlight: '#2563EB',       // Blue for secondary values on light bg
    textPrimary: '#1a202c',
    textSecondary: '#4a5568',
    textMuted: '#94a3b8',
    rowDivider: 'rgba(0,0,0,0.06)',
    barTrack: 'rgba(0,0,0,0.07)',
    financialRowBg: 'rgba(160,104,32,0.06)',
  },
  'priority-life-care': {
    label: 'Priority Life Care',
    pageBg: '#f0f7f7',
    cardBg: '#ffffff',
    cardBorder: 'rgba(0,0,0,0.08)',
    cardShadow: '0 1px 4px rgba(0,0,0,0.06)',
    accent: '#0D9488',          // Darkened teal — readable on white
    sectionTitle: '#4C4894',    // Dark lavender
    sectionDivider: 'rgba(76,72,148,0.2)',
    barHigh: '#0D9488',         // Dark teal for healthy occupancy
    barMid: '#B8960C',          // Dark sunflower for mid occupancy
    highlight: '#B8960C',       // Dark sunflower for secondary highlights
    textPrimary: '#1a202c',
    textSecondary: '#4a5568',
    textMuted: '#94a3b8',
    rowDivider: 'rgba(0,0,0,0.06)',
    barTrack: 'rgba(0,0,0,0.07)',
    financialRowBg: 'rgba(13,148,136,0.06)',
  },
  'dwa-internal': {
    label: 'DWA Creative Scorecard',
    pageBg: '#f8f8f6',
    cardBg: '#ffffff',
    cardBorder: 'rgba(0,0,0,0.08)',
    cardShadow: '0 1px 4px rgba(0,0,0,0.06)',
    accent: '#fec35c',          // DWA gold
    sectionTitle: '#1a202c',
    sectionDivider: 'rgba(26,32,44,0.18)',
    barHigh: '#5C6B44',
    barMid: '#fec35c',
    highlight: '#0ea5e9',
    textPrimary: '#1a202c',
    textSecondary: '#4a5568',
    textMuted: '#94a3b8',
    rowDivider: 'rgba(0,0,0,0.06)',
    barTrack: 'rgba(0,0,0,0.07)',
    financialRowBg: 'rgba(254,195,92,0.08)',
  },
};

// Top bar is always Lupa-branded, independent of client
const LUPA_GOLD = '#fec35c';
const TOPBAR: React.CSSProperties = {
  background: 'rgba(10,10,10,0.97)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
};

const REFRESH_MS = 60_000;

// ─── Adapters: backend snapshot shapes -> existing UI shapes ────────────────

function adaptOverviewToOccupancy(o: DashboardOverview | null): OccupancyData | null {
  if (!o) return null;
  const cg = o.by_care_group || {};
  // Aline care_group_type_name values seen in MorningStar: 'AL', 'MC', 'IL' (and possibly 'ALZ').
  // Map them onto the existing UI's three slots; everything else is rolled into the totals only.
  const pick = (...keys: string[]) =>
    keys.map(k => cg[k]).find(Boolean) ?? { total: 0, occupied: 0 };
  return {
    community: undefined,
    totalUnits: o.total_units,
    occupiedUnits: o.occupied_units,
    occupancyRate: o.occupancy_rate,
    byType: {
      independentLiving: pick('IL', 'Independent', 'Independent Living'),
      assistedLiving: pick('AL', 'Assisted', 'Assisted Living'),
      memoryCare: pick('MC', 'ALZ', 'Memory Care'),
    },
    asOf: o.last_snapshot_at ?? undefined,
  };
}

function adaptMoveIns(rows: DashboardMoveIn[] | null): MoveInsData | null {
  if (!rows) return null;
  return {
    moveIns: rows.map(r => ({
      id: String(r.contract_id),
      residentName: r.individual_name ?? '(unknown)',
      moveInDate: r.move_in_date ?? '',
      careType: r.care_group ?? '',
    })),
    total: rows.length,
  };
}

function adaptLeads(rows: DashboardLead[] | null): LeadsData | null {
  if (!rows) return null;
  return {
    leads: rows.map(r => ({
      id: String(r.case_id),
      prospectName:
        [r.first_name, r.last_name].filter(Boolean).join(' ').trim() ||
        r.case_number ||
        '(unknown)',
      inquiryDate: r.date_created ?? '',
      status: (r.status_name || 'unknown').toLowerCase(),
      careType: '',
      source: r.created_by_user ?? undefined,
    })),
    total: rows.length,
  };
}


// ─── Main component ───────────────────────────────────────────────────────────

export const MorningStarDashboardPage: React.FC<{ clientId: DashboardClientId }> = ({ clientId }) => {
  const theme = CLIENT_THEME[clientId];
  const backendClientId = useMemo(() => mapBackendClientId(clientId), [clientId]);
  // MorningStarDashboardPage is the standalone MS page. The isMorningStar
  // gate is true for the only client this page handles — kept for explicit
  // diff-locality with PLC + DWA Internal pages where the same shape lives.
  const isMorningStar = clientId === 'morningstar-senior-living';
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setErrors] = useState<Record<string, string>>({});
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Portfolio state (MorningStar only) ─────────────────────────────────────
  // Lives at the page level so it's the first thing users pick. The Weekly
  // view + (eventually) any other tab consume this.
  const { user, logout } = useAuth();
  const isClientRole = user?.role === 'client';
  // Portfolios the user is allowed to see for THIS client. DWA users see all;
  // client users see only their explicit allowlist for backendClientId.
  const allowedPortfolioKeys = useMemo<string[] | null>(() => {
    if (!user || user.role !== 'client') return null; // null = unrestricted
    const entry = (user.client_access || []).find(
      (e: ClientAccessEntry) => e.client_id === backendClientId,
    );
    return entry ? entry.portfolio_keys : [];
  }, [user, backendClientId]);

  const [portfoliosAll, setPortfoliosAll] = useState<PortfolioDef[]>([]);
  const portfolios = useMemo(() => {
    if (allowedPortfolioKeys === null) return portfoliosAll;
    const allowed = new Set(allowedPortfolioKeys);
    return portfoliosAll.filter(p => allowed.has(p.key));
  }, [portfoliosAll, allowedPortfolioKeys]);

  // Default to DWA Managed for MorningStar (DWA users) so leadership lands
  // on the operationally-owned portfolio first. Client users land on their
  // first allowed portfolio.
  const [portfolioKey, setPortfolioKey] = useState<string>(
    isMorningStar ? 'dwa-managed' : '',
  );
  // Which report view (weekly vs monthly) is active for MorningStar.
  const [reportView, setReportView] = useState<ReportView>('weekly');

  useEffect(() => {
    if (!isMorningStar) return;
    let cancelled = false;
    fetchPortfolios(backendClientId)
      .then(rows => { if (!cancelled) setPortfoliosAll(rows); })
      .catch(() => { /* portfolio strip just stays empty */ });
    return () => { cancelled = true; };
  }, [backendClientId, isMorningStar]);

  // For client users (or if the default 'dwa-managed' isn't allowed), pin the
  // portfolio to the first allowed key once the portfolio list resolves.
  useEffect(() => {
    if (portfolios.length === 0) return;
    if (!portfolios.find(p => p.key === portfolioKey)) {
      setPortfolioKey(portfolios[0].key);
    }
  }, [portfolios, portfolioKey]);

  // Theme adapter for the WeeklyReportView (it uses a slightly different theme contract).
  const weeklyTheme: WeeklyReportTheme = {
    pageBg: theme.pageBg,
    cardBg: theme.cardBg,
    cardBorder: theme.cardBorder,
    cardShadow: theme.cardShadow,
    accent: theme.accent,
    highlight: theme.highlight,
    sectionTitle: theme.sectionTitle,
    sectionDivider: theme.sectionDivider,
    textPrimary: theme.textPrimary,
    textSecondary: theme.textSecondary,
    textMuted: theme.textMuted,
    rowDivider: theme.rowDivider,
    positive: '#15803d',
    negative: '#dc2626',
  };

  const fetchData = useCallback(async () => {
    const errs: Record<string, string> = {};
    let isMock = false;

    async function safe<T>(key: string, fn: () => Promise<T>): Promise<T | null> {
      try {
        const result = await fn();
        if ((result as Record<string, unknown>)?._mock) isMock = true;
        return result;
      } catch (e) {
        errs[key] = e instanceof Error ? e.message : String(e);
        return null;
      }
    }

    let next: DashboardData;

    {
      // Aline-backed sections read from Postgres snapshots via /dashboard/morningstar/*.
      // TalkFurther + Yardi still come from MCP stubs and error until those
      // integrations are real — captured per-key in `errs`.
      const backendId = mapBackendClientId(clientId);
      const [overview, moveInsRaw, leadsRaw, talkFurther, financial] = await Promise.all([
        safe('overview',    () => fetchOverview(backendId)),
        safe('moveIns',     () => fetchMoveIns(backendId, 30, 50)),
        safe('leads',       () => fetchLeads(backendId, { limit: 50 })),
        safe('talkFurther', () => callMcpTool<TalkFurtherData>('crm_talk_further_leads', { dataType: 'all' }, 'morningstar')),
        safe('financial',   () => callMcpTool<YardiFinancial>('crm_yardi_data', { reportType: 'financial' }, 'morningstar')),
      ]);
      next = {
        occupancy: adaptOverviewToOccupancy(overview),
        leads: adaptLeads(leadsRaw),
        moveIns: adaptMoveIns(moveInsRaw),
        talkFurther,
        financial,
        isMock,
        lastUpdated: new Date(),
      };
    }

    setData(next);
    setErrors(errs);
    setLoading(false);
  }, [clientId]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    setErrors({});
    fetchData();
    intervalRef.current = setInterval(fetchData, REFRESH_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchData]);

  return (
    <div style={{ minHeight: '100vh', background: theme.pageBg, fontFamily: 'Lato, system-ui, sans-serif', color: theme.textPrimary }}>

      {/* ── Top bar — always Lupa branding ─────────────────────────────── */}
      <header style={{
        ...TOPBAR,
        padding: '0.875rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          {/* Wolf icon — same as main chat sidebar */}
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
            border: `1px solid rgba(254,195,92,0.3)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <img src={BrandIcon} alt="Lupa" style={{ width: 22, height: 22, objectFit: 'cover' }} />
          </div>

          {/* DWA users navigate via the shared Hub header (hubs/HubHeader.tsx). */}

          {/* Client identity — accent color from client theme */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              background: theme.accent, flexShrink: 0,
            }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0' }}>{theme.label}</span>
            <span style={{
              fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: theme.sectionTitle, background: `${theme.cardBg}`, border: `1px solid ${theme.cardBorder}`,
              padding: '0.1rem 0.45rem', borderRadius: '3px',
            }}>
              Dashboard
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {data?.lastUpdated && (
            <span style={{ fontSize: '0.7rem', color: '#4a5568' }}>
              Updated {data.lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            disabled={loading}
            style={{
              fontSize: '0.75rem', padding: '0.3rem 0.8rem', borderRadius: '4px', fontFamily: 'inherit',
              cursor: loading ? 'default' : 'pointer',
              background: loading ? 'rgba(255,255,255,0.04)' : `rgba(254,195,92,0.1)`,
              border: `1px solid ${loading ? 'rgba(255,255,255,0.08)' : 'rgba(254,195,92,0.3)'}`,
              color: loading ? '#4a5568' : LUPA_GOLD, fontWeight: 500,
            }}
          >
            {loading ? 'Loading…' : '⟳ Refresh'}
          </button>

          {/* Sign out — client-role users land here with no sidebar / user
              menu, so this is their only way to log out. */}
          <button
            onClick={() => { logout(); }}
            style={{
              fontSize: '0.75rem', padding: '0.3rem 0.8rem', borderRadius: '4px', fontFamily: 'inherit',
              cursor: 'pointer', background: 'none',
              border: '1px solid rgba(255,255,255,0.14)', color: '#a0aec0', fontWeight: 500,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#a0aec0'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; }}
          >
            Sign Out
          </button>
        </div>
      </header>

      <main style={{ padding: '1.5rem', maxWidth: '1440px', margin: '0 auto' }}>

        {/* Portfolio selector — hidden entirely for client-role users
            (they shouldn't see portfolio segmentation at all). DWA users
            see the selector when there are portfolios for this client. */}
        {isMorningStar && portfolios.length > 0 && !isClientRole && (
          <div style={{
            display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center',
            marginBottom: '1.25rem', padding: '0.6rem 0.8rem',
            background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
            borderRadius: 4, boxShadow: theme.cardShadow,
          }}>
            <span style={{
              fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: theme.textSecondary,
              marginRight: '0.5rem',
            }}>Portfolio</span>
            {portfolios.map(p => {
              const active = portfolioKey === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => setPortfolioKey(p.key)}
                  title={`${p.community_uniques.length} communities`}
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    padding: '0.45rem 1rem',
                    borderRadius: 3,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    background: active ? theme.accent : 'transparent',
                    color: active ? '#ffffff' : theme.textSecondary,
                    border: `1px solid ${active ? theme.accent : theme.cardBorder}`,
                  }}
                >
                  {p.label}
                  <span style={{
                    marginLeft: '0.4rem',
                    fontSize: '0.72rem',
                    opacity: 0.85,
                    fontWeight: 400,
                  }}>({p.community_uniques.length})</span>
                </button>
              );
            })}
          </div>
        )}

        {/* View tabs — Weekly / Monthly. Below the portfolio selector so the
            hierarchy reads: Portfolio → View → content. */}
        {isMorningStar && (
          <div style={{
            display: 'flex', gap: '0.4rem', marginBottom: '1rem', alignItems: 'center',
          }}>
            <span style={{
              fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: theme.textSecondary,
              marginRight: '0.5rem',
            }}>Report</span>
            {(['weekly', 'monthly', 'yoy', 'custom'] as ReportView[]).map(view => {
              const active = reportView === view;
              const viewLabel = view === 'weekly' ? 'Weekly'
                : view === 'monthly' ? 'Monthly'
                : view === 'yoy' ? 'YoY'
                : 'Custom';
              return (
                <button
                  key={view}
                  onClick={() => setReportView(view)}
                  style={{
                    fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.04em',
                    padding: '0.4rem 0.95rem', borderRadius: 3, cursor: 'pointer',
                    fontFamily: 'inherit',
                    background: active ? theme.sectionTitle : 'transparent',
                    color: active ? '#ffffff' : theme.textSecondary,
                    border: `1px solid ${active ? theme.sectionTitle : theme.cardBorder}`,
                  }}
                >
                  {viewLabel}
                </button>
              );
            })}
          </div>
        )}

        {/* MorningStar: render the chosen report. Portfolio above scopes it. */}
        {isMorningStar && reportView === 'weekly' && (
          <WeeklyReportView
            clientId={clientId}
            theme={weeklyTheme}
            portfolioKey={portfolioKey}
          />
        )}
        {isMorningStar && reportView === 'monthly' && (
          <MonthlyReportView
            clientId={clientId}
            theme={weeklyTheme as MonthlyReportTheme}
            portfolioKey={portfolioKey}
          />
        )}
        {isMorningStar && reportView === 'yoy' && (
          <MonthlyReportView
            clientId={clientId}
            theme={weeklyTheme as MonthlyReportTheme}
            portfolioKey={portfolioKey}
            compareMode="yoy"
          />
        )}
        {isMorningStar && reportView === 'custom' && (
          <CustomReportView
            clientId={clientId}
            theme={weeklyTheme as CustomReportTheme}
            portfolioKey={portfolioKey}
          />
        )}

      </main>
    </div>
  );
};

export default MorningStarDashboardPage;
